import React, { useState } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "src/components/form/Input";
import useMutation from "src/hooks/useMutation";
import { UPDATE_PROJECT_MUTATION } from "src/constants/graphql/mutations";
import useNotification from "src/hooks/notification";
import LogoUploadField from "./LogoUploadField";
import useAuth from "src/hooks/useAuth";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Chip from "@mui/material/Chip";
import { getProjectVariables } from "src/utils/profile/settings";

export default function ReportSettings({ project, storageEnabled }) {
  const { values } = useFormikContext();
  const { user } = useAuth();
  const { settings } = user.company;
  const { notifySuccess, notifyError } = useNotification();
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);
  const [loading, setLoading] = useState({
    partner: false,
    customer: false,
  });

  const hasAdvancedOptions = Boolean(
    project.osDEmailSendList ||
      project.outboundOnlyEmailSendList ||
      project.deliveryOnlyEmailSendList ||
      project.billingEmailSendList ||
      project.automatedReportsEmailSendList
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(hasAdvancedOptions);
  const shouldShowButton = !hasAdvancedOptions && !showAdvancedOptions;

  const [logoSelection, setLogoSelection] = useState(project.mainLogoSelection || "primary");

  const hasSubsidiaryLogo = Boolean(user.company.subsidiaryLogoUrl);
  const hasSubsidiary2Logo = Boolean(user.company.subsidiary2LogoUrl);
  const hasAnySubsidiaryLogo = hasSubsidiaryLogo || hasSubsidiary2Logo;

  const getCurrentLogo = () => {
    if (logoSelection === "subsidiary" && user.company.subsidiaryLogoUrl) {
      return {
        url: user.company.subsidiaryLogoUrl,
        label: "Subsidiary Logo",
      };
    }
    if (logoSelection === "subsidiary_2" && user.company.subsidiary2LogoUrl) {
      return {
        url: user.company.subsidiary2LogoUrl,
        label: "Subsidiary 2 Logo",
      };
    }
    return {
      url: user.company.labelsLogoUrl,
      label: "Main Logo",
    };
  };

  const getNextLogoSelection = (current, direction) => {
    const availableLogos = ["primary"];
    if (hasSubsidiaryLogo) availableLogos.push("subsidiary");
    if (hasSubsidiary2Logo) availableLogos.push("subsidiary_2");

    const currentIndex = availableLogos.indexOf(current);

    if (direction === "next") {
      return availableLogos[(currentIndex + 1) % availableLogos.length];
    } else {
      return availableLogos[(currentIndex - 1 + availableLogos.length) % availableLogos.length];
    }
  };

  const toggleLogo = direction => {
    if (!hasAnySubsidiaryLogo) return;

    const newSelection = getNextLogoSelection(logoSelection, direction);
    setLogoSelection(newSelection);
    updateLogoSelection(newSelection);
  };

  const updateLogoSelection = async newSelection => {
    try {
      const {
        partnerLogoUrl,
        customerLogoUrl,
        __typename,
        client,
        warehouses,
        costCenters,
        createdBy,
        createdAt,
        clientId,
        active,
        ...projectData
      } = project;
      const variables = {
        ...projectData,
        mainLogoSelection: newSelection,
      };

      if (variables.addresses) {
        variables.addresses = variables.addresses.map(({ __typename, ...address }) => address);
      }

      await updateProject({
        variables,
        onCompleted: () => {
          notifySuccess("Main logo selection updated successfully");
        },
      });
    } catch (error) {
      notifyError(error.message);
    }
  };

  const currentLogo = getCurrentLogo();

  const handleLogoUpload = async (type, file) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const variables = getProjectVariables(values);

      variables[type === "partner" ? "partnerLogoUrl" : "customerLogoUrl"] = file;

      await updateProject({
        variables,
        onCompleted: () => {
          notifySuccess(`${type === "partner" ? "Main" : "Customer"} logo updated successfully`);
        },
      });
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleLogoRemove = async type => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const variables = getProjectVariables(values);

      variables[type === "partner" ? "removePartnerLogoUrl" : "removeCustomerLogoUrl"] = true;

      await updateProject({
        variables,
        onCompleted: () => {
          notifySuccess(`${type === "partner" ? "Partner" : "Customer"} logo removed successfully`);
        },
      });
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  return (
    <Paper>
      <Box p={2}>
        {storageEnabled && (
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={500}>Report Settings</Typography>
            </Box>

            <Grid container spacing={1} sx={{ pt: 1.5 }}>
              <Grid size={12}>
                <Input
                  type="autoExpand"
                  name="customerEmailSendList"
                  label="General Notifications Email List"
                  showNAButton={false}
                />
              </Grid>

              <Grid size={12}>
                <Input
                  type="autoExpand"
                  name="approvedEmailSendList"
                  label="Notify Customer After Approval Email List"
                  showNAButton={false}
                />
              </Grid>

              {shouldShowButton && (
                <Grid size={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ExpandMoreIcon />}
                    onClick={() => setShowAdvancedOptions(true)}
                    sx={{ mt: 1 }}
                  >
                    Show More Email Options
                  </Button>
                </Grid>
              )}
              {(showAdvancedOptions || project.automatedReportsEmailSendList) && (
                <Grid size={12}>
                  <Input
                    type="autoExpand"
                    name="automatedReportsEmailSendList"
                    label="Automated Reports Email List"
                    showNAButton={false}
                  />
                </Grid>
              )}
              {(showAdvancedOptions || project.outboundOnlyEmailSendList) && (
                <Grid size={12}>
                  <Input
                    type="autoExpand"
                    name="outboundOnlyEmailSendList"
                    label="Outbound Only Email List"
                    showNAButton={false}
                  />
                </Grid>
              )}
              {settings.allow_delivery && (showAdvancedOptions || project.deliveryOnlyEmailSendList) && (
                <Grid size={12}>
                  <Input
                    type="autoExpand"
                    name="deliveryOnlyEmailSendList"
                    label="Delivery Only Email List"
                    showNAButton={false}
                  />
                </Grid>
              )}
              {(showAdvancedOptions || project.osDEmailSendList) && (
                <Grid size={12}>
                  <Input type="autoExpand" name="osDEmailSendList" label="OS&D Only Email List" showNAButton={false} />
                </Grid>
              )}
              {(showAdvancedOptions || project.billingEmailSendList) && (
                <Grid size={12}>
                  <Input
                    type="autoExpand"
                    name="billingEmailSendList"
                    label="Billing Only Email List"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
        <Box>
          <Typography fontWeight={400}>Report Logos</Typography>
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid size={{ xs: 12, md: settings.receiving_wizard ? 4 : 6 }}>
              <Box
                p={2}
                border="1px solid #e0e0e0"
                borderRadius={1}
                height="100%"
                display="flex"
                flexDirection="column"
                gap={2}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="textSecondary">
                    Main Logo
                  </Typography>
                  {hasAnySubsidiaryLogo && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => toggleLogo("prev")}
                        disabled={!hasAnySubsidiaryLogo}
                      >
                        <ArrowBackIosIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => toggleLogo("next")}
                        disabled={!hasAnySubsidiaryLogo}
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Box
                  flexGrow={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight={70}
                  position="relative"
                >
                  {currentLogo.url ? (
                    <img
                      src={currentLogo.url}
                      alt={currentLogo.label}
                      style={{ maxWidth: "100%", maxHeight: 70 }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No logo uploaded yet
                    </Typography>
                  )}
                </Box>

                <Box display="flex" justifyContent="center">
                  <Chip
                    label={currentLogo.label}
                    size="small"
                    color={
                      logoSelection === "subsidiary"
                        ? "secondary"
                        : logoSelection === "subsidiary_2"
                        ? "info"
                        : "primary"
                    }
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Grid>
            {settings.receiving_wizard && (
              <Grid size={{ xs: 12, md: 4 }}>
                <LogoUploadField
                  type="partner"
                  label="Partner Logo"
                  logoUrl={project.partnerLogoUrl}
                  loading={loading.partner}
                  onUpload={handleLogoUpload}
                  onRemove={handleLogoRemove}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: settings.receiving_wizard ? 4 : 6 }}>
              <LogoUploadField
                type="customer"
                label={`Customer Logo`}
                logoUrl={project.customerLogoUrl}
                loading={loading.customer}
                onUpload={handleLogoUpload}
                onRemove={handleLogoRemove}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
}
